import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  CreateProjectRequest,
  GetProjectsForSidebarResponse,
  ManageProjectRequest,
  UniqueCheckResponse,
} from "tng-address-collection-shared/types/apiTypes.ts";
import { useAuthentication } from "../auth/useAuthentication.ts";
import { NotUniqueError } from "./errors.ts";
import { queryKeys } from "./queryKeys.ts";

export const useCreateProject = () => {
  const { getToken } = useAuthentication();
  const queryClient = useQueryClient();
  const { error, isPending, mutate, isError, reset } = useMutation({
    mutationFn: async (
      project: CreateProjectRequest,
    ): Promise<{ id: string }> => {
      const token = await getToken();

      const uniqueCheckResponse = await fetch(
        `/api/projects/unique?displayName=${encodeURIComponent(project.displayName)}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!uniqueCheckResponse.ok) {
        throw new Error("Checking the project name failed");
      }

      const { isUnique } = UniqueCheckResponse.parse(
        await uniqueCheckResponse.json(),
      );

      if (!isUnique) {
        throw new NotUniqueError();
      }

      const response = await fetch(`/api/projects`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(project),
      });

      if (!response.ok) {
        throw new Error("Creating the project failed");
      }

      return (await response.json()) as { id: string };
    },
    onSuccess: (data, project) => {
      queryClient.setQueryData(
        queryKeys.projects(),
        (old: GetProjectsForSidebarResponse | undefined) =>
          [
            ...(old ?? []),
            { id: data.id, displayName: project.displayName },
          ].sort((a, b) => a.displayName.localeCompare(b.displayName)),
      );
      queryClient.setQueryData(queryKeys.manageProject(data.id), {
        ...project,
        id: data.id,
        infoBox: project.infoBox ?? "",
      } satisfies ManageProjectRequest);
    },
  });

  return { mutate, isPending, error, isError, reset };
};
